import classNames from "classnames";
import { useEffect, useRef } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { PlusIcon } from "components/icons/PlusIcon";
import ToolTip from "components/design/toolTip";
import { AnnotationDoc } from "./DocumentAnnotationPanel";
import { QuestionCard, SidebarQuestion } from "./QuestionCard";

type QuestionSidebarProps = {
  questions: SidebarQuestion[];
  activePinId: string | null;
  docs: AnnotationDoc[];
  onQuestionClick: (id: string) => void;
  onDeleteQuestion?: (id: string) => void;
  onChangeQuestion?: (id: string, text: string) => void;
  onRemoveAnnotation?: (id: string) => void;
  onAddQuestion?: () => void;
  pinModeId?: string | null;
  draftPinnedPosition?: { x: number; y: number; docIndex: number } | null;
  onEnterPinMode?: (id: string) => void;
  onExitPinMode?: () => void;
  renderQuestionExtra?: (question: SidebarQuestion) => React.ReactNode;
  variant?: "admin" | "answer";
  showHeader?: boolean;
  onViewDocument?: (id: string) => void;
  /** IDs of questions currently failing validation — each gets the red error ring. */
  errorQuestionIds?: string[];
  errorText?: string;
  /** ID to scroll into view when validation fails, usually the first error. */
  scrollToQuestionId?: string | null;
  compact?: boolean;
  topBanner?: React.ReactNode;
  bottomContent?: React.ReactNode;
  headerSubtext?: string;
  headerAction?: React.ReactNode;
  questionTextClassName?: string;
};

const EMPTY_QUESTIONS_TEXT = "No questions added yet";

export const QuestionSidebar = ({
  questions,
  activePinId,
  docs,
  onQuestionClick,
  onDeleteQuestion,
  onChangeQuestion,
  onRemoveAnnotation,
  onAddQuestion,
  pinModeId,
  onEnterPinMode,
  onExitPinMode,
  renderQuestionExtra,
  variant = "admin",
  showHeader = true,
  onViewDocument,
  errorQuestionIds,
  errorText,
  scrollToQuestionId,
  compact = false,
  topBanner,
  bottomContent,
  headerSubtext,
  headerAction,
  questionTextClassName,
}: QuestionSidebarProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activePinId || !listRef.current) return;
    const cardEl = listRef.current.querySelector(`#question-${activePinId}`);
    cardEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activePinId]);

  useEffect(() => {
    if (!scrollToQuestionId || !listRef.current) return;
    const cardEl = listRef.current.querySelector(
      `#question-${scrollToQuestionId}`
    );
    cardEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [scrollToQuestionId]);

  const handleCardTogglePinMode = (id: string) => {
    if (pinModeId === id) {
      onExitPinMode?.();
    } else {
      onEnterPinMode?.(id);
    }
  };

  return (
    <div
      className={classNames("t-flex t-flex-col", {
        "t-h-full t-overflow-hidden": !compact,
      })}
    >
      {showHeader && (
        <div className="t-flex t-items-center t-justify-between t-px-4 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-shrink-0 t-h-12">
          <div className="t-flex t-items-center t-gap-2">
            <span className="t-text-subtext t-font-medium t-text-text-100">
              Questions
            </span>
            {headerSubtext && (
              <span className="t-text-body-sm t-text-neutral-40 t-italic">
                {headerSubtext}
              </span>
            )}
          </div>
          {headerAction}
          {!headerAction && onAddQuestion && (
            <ToolTip text="Add new question">
              <Button size="xsmall" customType="icon" onClick={onAddQuestion}>
                <PlusIcon size="10" />
              </Button>
            </ToolTip>
          )}
        </div>
      )}

      <div
        ref={listRef}
        className={classNames("t-p-4 t-flex t-flex-col t-gap-2.5", {
          "t-min-h-0 t-flex-1 t-overflow-y-auto": !compact,
          "t-max-h-[60vh] t-overflow-y-auto": compact,
        })}
      >
        {topBanner}

        {questions.length === 0 && (
          <div className="t-text-body-sm t-text-text-30 t-text-center t-mt-8">
            {EMPTY_QUESTIONS_TEXT}
          </div>
        )}

        {questions.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            number={index + 1}
            isActive={activePinId === question.id}
            docs={docs}
            variant={variant}
            onClick={() => onQuestionClick(question.id)}
            onChange={
              onChangeQuestion
                ? (text) => onChangeQuestion(question.id, text)
                : undefined
            }
            onDelete={
              onDeleteQuestion ? () => onDeleteQuestion(question.id) : undefined
            }
            isPinMode={pinModeId === question.id}
            onTogglePinMode={
              onEnterPinMode
                ? () => handleCardTogglePinMode(question.id)
                : undefined
            }
            onRemoveAnnotation={
              onRemoveAnnotation
                ? () => onRemoveAnnotation(question.id)
                : undefined
            }
            onViewDocument={
              onViewDocument ? () => onViewDocument(question.id) : undefined
            }
            hasError={errorQuestionIds?.includes(question.id) ?? false}
            errorText={errorText}
            questionTextClassName={questionTextClassName}
          >
            {renderQuestionExtra?.(question)}
          </QuestionCard>
        ))}

        {onAddQuestion && variant === "admin" && (
          <Button
            customType="secondary"
            size="small"
            onClick={onAddQuestion}
            className="t-self-start"
            leftIcon={<PlusIcon color="currentColor" size="12" />}
          >
            Add question
          </Button>
        )}

        {bottomContent}
      </div>
    </div>
  );
};
